type IDate = IYearMonth & IDay;

interface IYearMonth {
  year: number;
  month: number;
}

interface IDay {
  day: number;
}

interface IBizDays extends IYearMonth {
  days: number[];
}

interface ICalendar {
  organizationId: string;
  updatedAt: number;
  workDays: IBizDays[];
  unRegisterCalendarAlert: {
    updateAt: number;
    isDisplay: boolean;
  };
}
/**
 * IYearMonth型をシリアライズする
 * @param iYearMonth 年月情報
 * @returns 年_月の文字列
 */
const serialize = (iYearMonth: IYearMonth) => iYearMonth.year + '_' + iYearMonth.month;

/**
 * 渡された月情報に'0'をつけて文字列として返す
 * '0'をつけるのは1桁月を対象とする
 * 2桁月はそのままの形で文字列にして返す
 * @param [month] 月情報
 * @returns 一桁の月の場合0付きで返す(例：7月->'07'、12月->'12')
 */
const convertMonthWithZero = (month: number | string): string => {
  let convertMonth = '';
  if (typeof month === 'string') {
    // 文字列の場合
    convertMonth = ('0' + month).slice(-2);
  } else {
    // 整数の場合
    convertMonth = ('0' + String(month)).slice(-2);
  }
  return convertMonth;
};

/**
 * 稼働日カレンダーの年度の初期値を設定する
 * @param date 日付情報
 */
const setDefaultSelectFiscalYear = (date: Date): number => {
  // 現在の年を取得
  const year = date.getFullYear();
  // 現在の月を取得
  const month = date.getMonth();
  // 月は0から始まるため、1月は0、4月は3となります。
  if (month < 3) {
    // 1月から3月の場合は、前の年度となります。
    return year - 1;
  } else {
    // それ以外の場合は現在の年となります。
    return year;
  }
};

/**
 * 西暦、月の組み合わせで構成された文字列から、西暦と月を分離してそれぞれをnumber型にして返す
 * 入力する情報は必ず、西暦、月を6桁で表現した形のものにして渡す
 * @param yearMonthStr 数字6桁で表現された西暦、月の情報の文字列
 * @returns 西暦、月を分離してnumber型にcast
 */
const parseStringYearMonth = (yearMonthStr: string): IYearMonth => {
  const output: IYearMonth = {
    year: 0,
    month: 0,
  };
  if (yearMonthStr.length !== 6) {
    return output;
  }
  const year = Number(yearMonthStr.substring(0, 4));
  const month = Number(yearMonthStr.substring(4, 6));
  if (isNaN(year) || isNaN(month)) {
    output.year = isNaN(year) ? 0 : year;
    output.month = isNaN(month) ? 0 : month;
    return output;
  }
  output.year = year;
  output.month = month;
  return output;
};

/**
 * もらったIYearMonthを拡張したオブジェクトの中に、引数year、monthと一致する(検索したい)年月の情報があるかを判定する
 * @param targetObj 比較対象のIYearMonthを拡張したオブジェクト
 * @param baseObj 比較するためのIYearMonthを拡張したオブジェクト
 */
const equalYearMonthInfo = <T extends IYearMonth, U extends IYearMonth>(targetObj: T, baseObj: U): boolean => {
  return targetObj.year === baseObj.year && targetObj.month === baseObj.month;
};

/**
 * もらったIDateを拡張したオブジェクトの中に、引数year、month、dayと一致する(検索したい)年月日の情報があるかを判定する
 * @param targetObj 比較対象のIDateを拡張したオブジェクト
 * @param baseObj 比較するためのIDateを拡張したオブジェクト
 */
const equalYearMonthDayInfo = <T extends IDate, U extends IDate>(targetObj: T, baseObj: U): boolean => {
  return targetObj.year === baseObj.year && targetObj.month === baseObj.month && targetObj.day === baseObj.day;
};

export type { IDate, IYearMonth, IDay, IBizDays, ICalendar };

export { serialize, convertMonthWithZero, setDefaultSelectFiscalYear, parseStringYearMonth, equalYearMonthInfo, equalYearMonthDayInfo };
